/**
 * Web push enrollment (§5.3). SaaS-only, HTTPS-only, and gesture-only.
 *
 * The permission prompt is the scarce resource here. Browsers punish sites that ask
 * early — Chrome auto-quiets origins with low grant rates, and a denial is close to
 * permanent because the reset lives several clicks into site settings. So nothing in
 * this module registers a service worker or asks for permission as a side effect of
 * rendering. `enable()` runs only from a click, and every other export is a read.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { config } from '../config';
import { useAuth } from './useAuth';
import { accountScoped } from '@/lib/activeAccount';
import { forgetPushAccount, pushAccount, rememberPushAccount } from '@/lib/pushAccounts';

const backendApi = config.backendApi;

/** Where the browser's own permission state stands. */
export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

export interface PushSupport {
  /** Every API this needs is present, and we are on a secure origin. */
  supported: boolean;
  permission: PushPermission;
  /**
   * The APIs are missing *because this is an iOS browser tab*, and installing the app to
   * the Home Screen would provide them.
   *
   * Distinguished from plain `unsupported` because it is the difference between "your
   * browser can't do this" and "here is the one thing you need to do". iOS exposes
   * `PushManager` only to Home Screen web apps, so every iPhone user sees the missing-API
   * state until they install — and telling them the feature does not exist is wrong.
   */
  needsInstall: boolean;
}

/**
 * What this browser can do, read synchronously — no network, no registration.
 *
 * `isSecureContext` is not a designed UX state (§6): the product is HTTPS-only. It is
 * checked so an odd deployment renders an explanation instead of throwing on a missing
 * `navigator.serviceWorker`.
 */
export function usePushSupport(): PushSupport {
  const [permission, setPermission] = useState<PushPermission>(() => readPermission());

  // The user can change permission in site settings while the page is open, and there is
  // no event for it. Re-reading on focus is enough to keep the UI honest without polling.
  useEffect(() => {
    const refresh = () => { setPermission(readPermission()); };
    window.addEventListener('focus', refresh);
    return () => { window.removeEventListener('focus', refresh); };
  }, []);

  const supported = permission !== 'unsupported';
  return { supported, permission, needsInstall: !supported && isIosBrowserTab() };
}

/**
 * An iOS browser that is not running from the Home Screen.
 *
 * Every browser on iOS is WebKit underneath and none of them expose the Push API from a
 * tab, so this matches the platform rather than the browser. iPadOS reports a desktop UA,
 * hence the touch-points check.
 */
function isIosBrowserTab(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (!isIos) return false;
  const standalone =
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return !standalone;
}

function readPermission(): PushPermission {
  if (typeof window === 'undefined') return 'unsupported';
  if (!window.isSecureContext) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

interface VapidKeyResponse {
  publicKey: string;
}

/**
 * The deployment's `applicationServerKey`.
 *
 * A 503 means this deployment has no key provisioned, which is a valid state rather than
 * an error — it resolves to `null` so the UI can say "not available here" instead of
 * showing a failure. Cached for the session: the key is fixed for the deployment's life
 * and must never change, so there is nothing to revalidate.
 */
export function useVapidKey() {
  return useQuery<string | null>({
    queryKey: ['push-vapid-key'],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      try {
        const res = await axios.get<VapidKeyResponse>(`${backendApi}/api/notifications/push/vapid-public-key`, {
          withCredentials: true,
        });
        return res.data.publicKey;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 503) return null;
        throw error;
      }
    },
  });
}

export interface PushDevice {
  id: string;
  displayName: string;
  createdAt: string;
}

interface DestinationsResponse {
  destinations: { id: string; kind: string; displayName: string; createdAt: string }[];
}

/**
 * The browsers enrolled on this account.
 *
 * Read from the destinations list rather than a dedicated endpoint — a push subscription
 * *is* a destination, and that list already excludes retired rows, so a browser a push
 * service has declared gone disappears from here without any client-side filtering.
 */
export function usePushDevices() {
  return useQuery<PushDevice[]>({
    queryKey: ['push-devices'],
    queryFn: async () => {
      const res = await axios.get<DestinationsResponse>(`${backendApi}/api/notifications/destinations`, {
        withCredentials: true,
      });
      return res.data.destinations
        .filter((d) => d.kind === 'web_push')
        .map((d) => ({ id: d.id, displayName: d.displayName, createdAt: d.createdAt }));
    },
  });
}

/** base64url → the `Uint8Array` `pushManager.subscribe()` wants. */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

/** Register the push-only worker at the origin scope. Idempotent by browser contract. */
async function registerWorker(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register('/sw.js');
  // `subscribe()` on a worker that is still installing rejects, so wait it out.
  await navigator.serviceWorker.ready;
  return registration;
}

/**
 * The device model, where the browser is willing to say.
 *
 * `userAgentData` is Chromium-only and its high-entropy fields need an explicit, async
 * request — but on Android `model` is the actual handset ("Pixel 7"), which is the only
 * thing that tells two phones of the same make apart. Desktop Chromium returns an empty
 * model, and Safari and Firefox have no `userAgentData` at all, so every one of those
 * falls back to the platform from the user-agent string.
 */
async function deviceModel(): Promise<string | null> {
  interface UserAgentData {
    getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }>;
  }
  const uaData = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return null;

  try {
    const { model } = await uaData.getHighEntropyValues(['model']);
    return model && model.trim() !== '' ? model.trim() : null;
  } catch {
    // Permission-policy or an older implementation. Not worth reporting — the caller has
    // a perfectly good fallback.
    return null;
  }
}

/** Browser and platform from the user-agent string, which every browser has. */
function describeFromUserAgent(): { browser: string; platform: string | null } {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Unknown browser';
  const platform = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(ua)
      ? 'iOS'
      : /Macintosh/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;
  return { browser, platform };
}

/**
 * A label a person can tell apart in a device list.
 *
 * Sent on **every** announcement, not only the first. The server treats an absent name as
 * "leave it alone" rather than a default, and re-sending keeps the label current if the
 * browser is later updated or the device reports itself better.
 *
 * A machine's hostname is not available to a web page, so this is as specific as the
 * platform allows: "Chrome on Pixel 7" where the browser will name the handset, and
 * "Firefox on Linux" where it will not. Two identical browsers on two identical operating
 * systems are genuinely indistinguishable from here — the enrolment date in the list is
 * what separates those.
 */
async function describeBrowser(): Promise<string> {
  const { browser, platform } = describeFromUserAgent();
  const where = (await deviceModel()) ?? platform;
  return where ? `${browser} on ${where}` : browser;
}

/**
 * Announce a subscription as this tab's account and return the id of its hold.
 *
 * Each account signed in to the browser holds the endpoint separately
 * (docs/MultiAccountSessions.md §8). `onlyIfHeld` refreshes this account's hold without
 * creating one, and resolves `null` when it has none.
 */
async function postSubscription(
  subscription: PushSubscription,
  displayName?: string,
  onlyIfHeld = false,
): Promise<string | null> {
  const json = subscription.toJSON();
  try {
    const res = await axios.post<{ id: string }>(
      `${backendApi}/api/notifications/push/subscriptions`,
      {
        endpoint: json.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
        ...(displayName ? { displayName } : {}),
        ...(onlyIfHeld ? { onlyIfHeld } : {}),
      },
      { withCredentials: true },
    );
    return res.data.id;
  } catch (e) {
    if (onlyIfHeld && axios.isAxiosError(e) && e.response?.status === 404) return null;
    throw e;
  }
}

const BANNER_DISMISSED_KEY = 'push-enable-banner-dismissed';

/** Whether this account has said no to the enable-notifications nudge. */
export function enableBannerDismissed(): boolean {
  try {
    return localStorage.getItem(accountScoped(BANNER_DISMISSED_KEY)) === '1';
  } catch {
    // Private mode and hardened settings can throw on access; showing the nudge is a
    // better failure than breaking the shell.
    return false;
  }
}

export function dismissEnableBanner(): void {
  try {
    localStorage.setItem(accountScoped(BANNER_DISMISSED_KEY), '1');
  } catch {
    // Not persisting the dismissal is a smaller problem than a thrown error.
  }
}

/**
 * Turn on push for this browser. **Call from a click and nowhere else.**
 *
 * Order matters: permission is requested before subscribing, because `subscribe()` on a
 * default-permission browser triggers its own prompt in some engines and we want the
 * denial handled in one place.
 */
export function useEnablePush() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (vapidKey: string) => {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error(`permission-${permission}`);

      const registration = await registerWorker();
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: decodeKey(vapidKey),
        }));

      await postSubscription(subscription, await describeBrowser());
      if (user) await rememberPushAccount({ id: user.id, label: user.email, endpoint: subscription.endpoint });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['push-devices'] });
      void queryClient.invalidateQueries({ queryKey: ['push-local-device'] });
    },
  });
}

/**
 * This account's hold on *this* browser, if it has one — and, in finding out, repair it.
 * `null` means the account hasn't turned push on here.
 *
 * The subscription's endpoint is deliberately never sent to the client (it is a bearer
 * credential; the destinations view renders push rows as "browser push subscription"),
 * so the client cannot match itself against the list by comparing addresses. What it can
 * do is announce the subscription it holds and read back the id the server put it on.
 *
 * Runs on every app load (`EnableNotificationsBanner` mounts it in the shell), and the
 * announcement is **self-healing, not a no-op**:
 *
 * - It binds the hold to this tab's session, which is what keeps it delivering
 *   (docs/MultiAccountSessions.md §8). A hold whose session ended — the account signed out
 *   of this browser, or its session expired — comes back when the account signs in again.
 * - It never creates a hold. A browser signed in to several accounts belongs to all of
 *   them, and opening the app as one must not turn push on for it (`onlyIfHeld`).
 * - Except after a rotation. `pushsubscriptionchange` in the worker re-enrols each account
 *   when the push service rotates the endpoint, but it fires whenever the browser decides
 *   to, and a failed request there loses the account's push silently. A recorded endpoint
 *   that no longer matches is that case, and announcing the new one in full repairs it.
 *   A matching endpoint the backend no longer holds was removed on purpose, from another
 *   device's list, and stays removed.
 *
 * Quiet by design — no prompt, no registration that is not already there. `retry: false`
 * because a failure means "we could not confirm which device this is", which the UI
 * renders by simply not labelling a row.
 */
export function useLocalPushDevice(enabled: boolean) {
  const { user } = useAuth();
  const accountId = user?.id;
  const email = user?.email ?? '';
  return useQuery<string | null>({
    queryKey: ['push-local-device', accountId, email],
    enabled: enabled && !!accountId,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      if (!accountId) return null;
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription) return null;
      const recorded = await pushAccount(accountId);
      const rotated = recorded !== undefined && recorded.endpoint !== subscription.endpoint;
      // The name goes with every announcement. The server leaves an omitted name
      // alone, but re-sending keeps it fresh rather than frozen at first enrolment.
      const id = await postSubscription(subscription, await describeBrowser(), !rotated);
      if (id === null) {
        await forgetPushAccount(accountId);
      } else {
        await rememberPushAccount({ id: accountId, label: email, endpoint: subscription.endpoint });
      }
      return id;
    },
  });
}

/**
 * Send a test notification to one destination.
 *
 * Goes through the same channel a real notification does, so what it proves is what
 * delivery will do — a probe with its own code path could pass while delivery failed.
 *
 * Rate-limited server-side per destination (429 with `Retry-After`), and the server
 * answers 200 with `delivered: false` for a refusal it wants the user to act on, so both
 * outcomes are surfaced rather than only transport errors.
 */
/**
 * Re-exported so the push components keep one import site, but *defined* in
 * `useNotifications` — a test delivery is a destination operation, not a push one, and
 * the email rows need it too. Defining it here would force the destinations list to
 * import this module, which drags the whole push feature into the trial bundle and
 * trips `check-trial-bundle.mjs` on `pushManager`.
 */
export { useTestDestination, type TestDeliveryResult } from './useNotifications.ts';

/**
 * Stop this account's push on one browser.
 *
 * On *this* browser it also unsubscribes locally, unless another account signed in here
 * still receives push on it (docs/MultiAccountSessions.md §8): the endpoint is shared, and
 * unsubscribing it would end their push too. Otherwise deleting the row alone would leave
 * the browser holding a live subscription the backend no longer knows about — nothing
 * would arrive, but the permission and the endpoint would linger. The account's record and
 * the enable nudge go too, so nothing here offers it straight back.
 */
export function useDisablePush() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, thisBrowser }: { id: string; thisBrowser: boolean }) => {
      const res = await axios.delete<{ browserStillEnrolled?: boolean }>(
        `${backendApi}/api/notifications/push/subscriptions/${id}`,
        { withCredentials: true },
      );
      if (thisBrowser) {
        if (user) await forgetPushAccount(user.id);
        dismissEnableBanner();
        if (!res.data.browserStillEnrolled) {
          const registration = await navigator.serviceWorker.getRegistration('/sw.js');
          const subscription = await registration?.pushManager.getSubscription();
          await subscription?.unsubscribe();
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['push-devices'] });
      void queryClient.invalidateQueries({ queryKey: ['push-local-device'] });
    },
  });
}
